import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Fade,
  Backdrop,
  Portal,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';

interface TourStep {
  title: string;
  content: string;
  target?: string; // CSS selector for the element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: string; // Optional action text
}

interface ExpenseFeatureTourProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  steps?: TourStep[];
}

const DEFAULT_TOUR_STEPS: TourStep[] = [
  {
    title: "Welcome to Expense Management",
    content: "Track all your property-related expenses in one organized place. Let's take a quick tour of the key features.",
    target: "[data-tour='expense-header']",
    position: "bottom"
  },
  {
    title: "Add New Expenses",
    content: "Click here to add one-time expenses, set up recurring expenses, or create custom categories for your specific needs.",
    target: "[data-tour='add-expense-button']",
    position: "bottom",
    action: "Try clicking 'Add Expense'"
  },
  {
    title: "Expense Categories",
    content: "Organize expenses using predefined categories like utilities and maintenance, or create custom categories for unique property needs.",
    target: "[data-tour='categories-section']",
    position: "right"
  },
  {
    title: "Recurring Expenses",
    content: "Set up automatic tracking for regular expenses. Choose 'Fixed' for consistent amounts or 'Variable' for changing amounts with reminders.",
    target: "[data-tour='recurring-section']",
    position: "left"
  },
  {
    title: "Expense List & Filters",
    content: "View all your expenses here. Use filters to find specific expenses by date, category, or property. Click any expense to edit it.",
    target: "[data-tour='expense-list']",
    position: "top"
  },
  {
    title: "Reports & Analytics",
    content: "Generate detailed reports to analyze spending patterns, track trends, and export data for tax preparation.",
    target: "[data-tour='reports-section']",
    position: "top",
    action: "Explore the Reports tab"
  },
  {
    title: "Notifications & Reminders",
    content: "The notification badge shows pending expense reminders. Click it to see what needs your attention.",
    target: "[data-tour='notifications-badge']",
    position: "bottom"
  },
  {
    title: "You're All Set!",
    content: "You now know the basics of expense management. Start by adding a few expenses to get familiar with the system. Need help? Click the help icon anytime.",
    target: "[data-tour='help-button']",
    position: "left"
  }
];

const ExpenseFeatureTour: React.FC<ExpenseFeatureTourProps> = ({
  open,
  onClose,
  onComplete,
  steps = DEFAULT_TOUR_STEPS,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && steps[activeStep]?.target) {
      const element = document.querySelector(steps[activeStep].target!);
      if (element) {
        setHighlightedElement(element);
        
        // Calculate tooltip position
        const rect = element.getBoundingClientRect();
        const position = steps[activeStep].position || 'bottom';
        
        let top = 0;
        let left = 0;
        
        switch (position) {
          case 'top':
            top = rect.top - 20;
            left = rect.left + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + 20;
            left = rect.left + rect.width / 2;
            break;
          case 'left':
            top = rect.top + rect.height / 2;
            left = rect.left - 20;
            break;
          case 'right':
            top = rect.top + rect.height / 2;
            left = rect.right + 20;
            break;
        }
        
        setTooltipPosition({ top, left });
        
        // Scroll element into view
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }
    } else {
      setHighlightedElement(null);
    }
  }, [open, activeStep, steps]);

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  const handleSkip = () => {
    onClose();
  };

  const renderHighlight = () => {
    if (!highlightedElement) return null;
    
    const rect = highlightedElement.getBoundingClientRect();
    
    return (
      <Portal>
        <Box
          sx={{
            position: 'fixed',
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            border: 3,
            borderColor: 'primary.main',
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 9999,
            boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.2)',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)',
              },
              '70%': {
                boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
              },
            },
          }}
        />
      </Portal>
    );
  };

  const renderTooltip = () => {
    const currentStep = steps[activeStep];
    if (!currentStep) return null;
    
    const isLastStep = activeStep === steps.length - 1;
    const position = currentStep.position || 'bottom';
    
    // Calculate transform based on position
    let transform = 'translate(-50%, 0)';
    if (position === 'left') {
      transform = 'translate(-100%, -50%)';
    } else if (position === 'right') {
      transform = 'translate(0, -50%)';
    } else if (position === 'top') {
      transform = 'translate(-50%, -100%)';
    }
    
    return (
      <Portal>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform,
            maxWidth: 320,
            p: 3,
            zIndex: 10000,
            border: 2,
            borderColor: 'primary.main',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pr: 1 }}>
              {currentStep.title}
            </Typography>
            <IconButton size="small" onClick={handleSkip}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
            {currentStep.content}
          </Typography>
          
          {currentStep.action && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                💡 {currentStep.action}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {activeStep + 1} of {steps.length}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep > 0 && (
                <Button
                  size="small"
                  startIcon={<BackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              )}
              
              <Button
                variant="contained"
                size="small"
                endIcon={isLastStep ? <CompleteIcon /> : <NextIcon />}
                onClick={handleNext}
              >
                {isLastStep ? 'Finish' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Portal>
    );
  };

  if (!open) return null;

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: 9998,
          bgcolor: 'rgba(0, 0, 0, 0.3)',
        }}
        onClick={handleSkip}
      />
      
      {renderHighlight()}
      {renderTooltip()}
    </>
  );
};

// Hook to manage tour state
export const useExpenseFeatureTour = () => {
  const [tourOpen, setTourOpen] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);

  useEffect(() => {
    // Check if user has completed the tour before
    const completed = localStorage.getItem('expense-tour-completed');
    if (completed) {
      setTourCompleted(true);
    }
  }, []);

  const startTour = () => {
    setTourOpen(true);
  };

  const closeTour = () => {
    setTourOpen(false);
  };

  const completeTour = () => {
    setTourCompleted(true);
    localStorage.setItem('expense-tour-completed', 'true');
  };

  const resetTour = () => {
    setTourCompleted(false);
    localStorage.removeItem('expense-tour-completed');
  };

  return {
    tourOpen,
    tourCompleted,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
};

export default ExpenseFeatureTour;